import { useEffect, useRef, type KeyboardEvent } from "react";
import type { Inline } from "@talentkontor/domain";
import { editorZuInline, inlineZuEditorHtml, type ChipLabel, type MiniNode } from "./modell";

/** Einfügen eines Chips an der gemerkten Schreibmarke (Feldauswahl über „{“). */
export type ChipEinfuegen = (node: Extract<Inline, { t: "field" | "if" }>) => void;

/** Liegt die Schreibmarke in einem Chip, kommt sie dahinter (in einem Chip darf nichts stehen). */
function ausChipHeraus(range: Range): Range {
  const start = range.startContainer;
  const el = (start.nodeType === 1 ? (start as Element) : start.parentElement)?.closest("[data-node]");
  if (!el) return range;
  const r = document.createRange();
  r.setStartAfter(el);
  r.collapse(true);
  return r;
}

/** Schreibmarke am Anfang bzw. am Ende des Felds? (für ↑/↓ zwischen Bausteinen) */
function amRand(el: HTMLElement, richtung: "anfang" | "ende"): boolean {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || !sel.isCollapsed) return false;
  const r = sel.getRangeAt(0);
  const probe = document.createRange();
  probe.selectNodeContents(el);
  if (richtung === "anfang") probe.setEnd(r.startContainer, r.startOffset);
  else probe.setStart(r.endContainer, r.endOffset);
  return probe.toString().replace(/​/g, "").length === 0;
}

/** Nächstes bzw. voriges Textfeld im Dokument fokussieren. */
export function springe(von: HTMLElement, richtung: -1 | 1): boolean {
  const alle = Array.from(document.querySelectorAll<HTMLElement>(".bk-papier .bk-text[contenteditable='true']"));
  const i = alle.indexOf(von);
  const ziel = alle[i + richtung];
  if (!ziel) return false;
  ziel.focus();
  const s = window.getSelection();
  const r = document.createRange();
  r.selectNodeContents(ziel);
  r.collapse(richtung === -1 ? false : true);
  s?.removeAllRanges();
  s?.addRange(r);
  return true;
}

/**
 * Bearbeitbarer Text eines Bausteins: Fließtext mit Feld-Chips, die sich nicht bearbeiten lassen. Fett, kursiv und
 * unterstrichen über Strg/Cmd+B, I, U; Umschalt+Enter macht einen Zeilenumbruch, Enter einen neuen Absatz dahinter.
 * „/“ in einem leeren Feld öffnet das Baustein-Menü, „{“ die Feldauswahl, ↑/↓ am Rand wechseln den Baustein.
 * Eingefügt wird nur reiner Text.
 */
export function TextFeld({ value, onChange, label, chip, onSlash, onEnter, onFeld, onFocus, className, placeholder, readOnly }: {
  value: readonly Inline[];
  onChange: (v: Inline[]) => void;
  label: string;
  chip: ChipLabel;
  onSlash?: () => void;
  onEnter?: () => void;
  onFeld?: (einfuegen: ChipEinfuegen) => void;
  onFocus?: () => void;
  className?: string;
  placeholder?: string;
  readOnly?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const zuletzt = useRef<string | null>(null);

  // Neu zeichnen nur, wenn sich Inhalt oder Beschriftung der Chips geändert haben (nie während des Tippens)
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const html = inlineZuEditorHtml(value, chip);
    if (html === zuletzt.current) return;
    const fokus = document.activeElement === el;
    zuletzt.current = html;
    el.innerHTML = html;
    if (fokus) {
      const s = window.getSelection();
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false);
      s?.removeAllRanges();
      s?.addRange(r);
    }
  }, [value, chip]);

  const melde = () => {
    const el = ref.current;
    if (!el) return;
    const v = editorZuInline(el as unknown as MiniNode);
    zuletzt.current = inlineZuEditorHtml(v, chip);
    onChange(v);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (readOnly || e.nativeEvent.isComposing) return;
    const el = ref.current;
    if (!el) return;
    // Schreibmarke nie in einem Chip
    const sel = window.getSelection();
    if (sel && sel.rangeCount && el.contains(sel.anchorNode) && e.key.length === 1) {
      const r = sel.getRangeAt(0);
      const raus = ausChipHeraus(r);
      if (raus !== r) { sel.removeAllRanges(); sel.addRange(raus); }
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.shiftKey) { document.execCommand("insertLineBreak"); melde(); return; }
      onEnter?.();
      return;
    }
    if ((e.key === "ArrowUp" && amRand(el, "anfang")) || (e.key === "ArrowDown" && amRand(el, "ende"))) {
      if (springe(el, e.key === "ArrowUp" ? -1 : 1)) e.preventDefault();
      return;
    }
    if (e.key === "/" && onSlash && !(el.textContent ?? "").replace(/​/g, "").trim() && !el.querySelector("[data-node]")) {
      e.preventDefault();
      onSlash();
      return;
    }
    if (e.key === "{" && onFeld) {
      e.preventDefault();
      const s = window.getSelection();
      const range = s && s.rangeCount && el.contains(s.anchorNode) ? ausChipHeraus(s.getRangeAt(0).cloneRange()) : null;
      onFeld((node) => {
        const box = ref.current;
        if (!box) return;
        const tpl = document.createElement("template");
        tpl.innerHTML = `${inlineZuEditorHtml([node], chip).replace(/​$/, "")}​`;
        const frag = tpl.content;
        const last = frag.lastChild;
        if (range && box.contains(range.startContainer)) {
          range.deleteContents();
          range.insertNode(frag);
        } else {
          box.appendChild(frag);
        }
        if (last) {
          const s2 = window.getSelection();
          const r = document.createRange();
          r.setStartAfter(last);
          r.collapse(true);
          s2?.removeAllRanges();
          s2?.addRange(r);
        }
        box.focus();
        melde();
      });
    }
  };

  return (
    <div
      ref={ref}
      className={`bk-text ${className ?? ""}`}
      contentEditable={!readOnly}
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label={label}
      aria-readonly={readOnly || undefined}
      data-placeholder={placeholder}
      tabIndex={0}
      onInput={melde}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onPaste={(e) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        document.execCommand("insertText", false, text);
        melde();
      }}
      onDrop={(e) => e.preventDefault()}
    />
  );
}
